/**
 * Codelists in a discovery answer: the codes a target offers for each setting of a mapping group,
 * what an empty setting resolves to, and everything else, which the pick panel offers as before.
 */
import { isPlainObject, omit } from 'es-toolkit';
import { isConfigMappingGroup } from '@insio/shared-types';
import type { ConfigSchema } from '../types';
import {
  NO_DISCOVERED_DEFAULTS,
  type DiscoveredChoice,
  type DiscoveredDefaults
} from './use-discovered-defaults';

/**
 * Where a mapper's codes stand: never asked, the last ask failed, answered for the values in the
 * form, answered for another target, or not live.
 */
export const CodelistStatus = {
  NONE: 'none',
  FAILED: 'failed',
  LIVE: 'live',
  STALE: 'stale',
  SNAPSHOT: 'snapshot'
} as const;

export type CodelistStatus = (typeof CodelistStatus)[keyof typeof CodelistStatus];

/** One code a target offers for a mapping setting. */
export interface CodelistEntry {
  value: string;
  /** `KOD — Název`, as the target names it. */
  label: string;
  source: string;
}

/** What a discovery answer offers the mapper. */
export interface CodelistOffer {
  /** Mapping key → its codes, in the target's order: empty when it has none, absent if not said. */
  lists: Readonly<Record<string, readonly CodelistEntry[]>>;
  /** Mapping key → the code an empty value resolves to. */
  automatic: DiscoveredDefaults;
}

export const NO_CODELISTS: CodelistOffer = { lists: {}, automatic: NO_DISCOVERED_DEFAULTS };

/** The keys a form shows as rows of a mapping table. */
export function mappingKeysOf(schema: ConfigSchema): ReadonlySet<string> {
  return new Set(
    Object.entries(schema.properties ?? {})
      .filter(([, property]) => isConfigMappingGroup(property.group) && property.secret !== true)
      .map(([key]) => key)
  );
}

/** A choice as far as telling a codelist apart needs it. */
interface NamedChoice {
  name: string;
  options: readonly { values: Readonly<Record<string, unknown>> }[];
}

/** A codelist: named by a mapping key, and every option fills exactly that key. */
export function isCodelistChoice(choice: NamedChoice, mappingKeys: ReadonlySet<string>): boolean {
  if (!mappingKeys.has(choice.name)) return false;
  return choice.options.every(option => {
    const keys = Object.keys(option.values);
    return keys.length === 1 && keys[0] === choice.name;
  });
}

/** A discovery answer split between the mapper and everything else. */
export interface PartitionedDiscovery {
  codelists: CodelistOffer;
  /** The defaults of settings outside the mapping groups. */
  defaults: DiscoveredDefaults;
  /** The choices that are no codelist, never filling a mapping key. */
  choices: DiscoveredChoice[];
}

/**
 * Codelists and mapping-key defaults go to the mapper, the rest to the pick panel. The first list
 * of a key wins, a code is offered once, and a list of no option says the key has no code.
 */
export function partitionDiscovered(
  schema: ConfigSchema,
  found: { defaults: DiscoveredDefaults; choices: readonly DiscoveredChoice[] }
): PartitionedDiscovery {
  const mappingKeys = mappingKeysOf(schema);
  const lists: Record<string, CodelistEntry[]> = {};
  const choices: DiscoveredChoice[] = [];

  for (const choice of found.choices) {
    if (isCodelistChoice(choice, mappingKeys)) {
      if (Object.hasOwn(lists, choice.name)) continue;
      const entries = new Map<string, CodelistEntry>();
      for (const option of choice.options) {
        const value = option.values[choice.name]?.value;
        if (value === undefined || entries.has(value)) continue;
        entries.set(value, { value, label: option.label, source: option.source });
      }
      lists[choice.name] = [...entries.values()];
      continue;
    }
    const rest = withoutMappingValues(choice, mappingKeys);
    if (rest) choices.push(rest);
  }

  const automatic: DiscoveredDefaults = {};
  const defaults: DiscoveredDefaults = {};
  for (const [key, entry] of Object.entries(found.defaults)) {
    if (mappingKeys.has(key)) automatic[key] = entry;
    else defaults[key] = entry;
  }
  return { codelists: { lists, automatic }, defaults, choices };
}

/** A choice whose options fill no mapping key; null when nothing is left of it. */
export function withoutMappingValues(
  choice: DiscoveredChoice,
  mappingKeys: ReadonlySet<string>
): DiscoveredChoice | null {
  const fillsMapping = choice.options.some(option =>
    Object.keys(option.values).some(key => mappingKeys.has(key))
  );
  if (!fillsMapping) return choice;
  const options = choice.options
    .map(option => ({ ...option, values: omit(option.values, [...mappingKeys]) }))
    .filter(option => Object.keys(option.values).length > 0);
  return options.length > 0 ? { ...choice, options } : null;
}

/** The defaults of settings outside the mapping groups; the same object when there are none. */
export function withoutMappingKeys(
  schema: ConfigSchema,
  defaults: DiscoveredDefaults
): DiscoveredDefaults {
  const mappingKeys = mappingKeysOf(schema);
  if (!Object.keys(defaults).some(key => mappingKeys.has(key))) return defaults;
  return omit(defaults, [...mappingKeys]);
}

/**
 * The typed values with the mapping keys as saved, so that a reload of a saved connection whose
 * rows were edited still goes with its stored secrets.
 */
export function withSavedMappingValues(
  schema: ConfigSchema,
  typed: Record<string, unknown>,
  saved: unknown
): Record<string, unknown> {
  const mappingKeys = mappingKeysOf(schema);
  if (mappingKeys.size === 0) return typed;
  const stored = isPlainObject(saved) ? saved : {};
  const values = { ...typed };
  for (const key of mappingKeys) {
    if (Object.hasOwn(stored, key)) values[key] = stored[key];
    else delete values[key];
  }
  return values;
}
